import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { consumeAuthEntrypoint, trackUiEvent } from '../utils/uiAnalytics.js'
import { Button, Card, CardTitle, Input } from '../design-system/primitives/index.js'
import StateBanner from './StateBanner/StateBanner.jsx'

const REMEMBER_ME_STORAGE_KEY = 'remembered_email'

const passwordToggleStyle = {
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 8px',
  color: 'var(--text-secondary)',
  fontSize: 'var(--text-base)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'auto',
  minWidth: 'auto',
}

function Login({
  onLoginSuccess,
  error: externalError,
  onSwitchToRegister,
  guestKitchenWarning = false,
  embedded = false,
  idPrefix = '',
}) {
  const fieldId = (name) => (idPrefix ? `${idPrefix}-${name}` : name)
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(externalError || '')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Load remembered email from localStorage on mount.
  useEffect(() => {
    try {
      const rememberedEmail = localStorage.getItem(REMEMBER_ME_STORAGE_KEY)
      if (rememberedEmail) {
        setEmail(rememberedEmail)
        setRememberMe(true)
      }
    } catch {
      // Ignore storage access issues and continue with blank form.
    }
  }, [])

  React.useEffect(() => {
    setError(externalError || '')
  }, [externalError])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setEmailError('')
    setPasswordError('')
    setLoading(true)

    try {
      await login(email, password, rememberMe)

      trackUiEvent('auth_login_completed', { auth_entrypoint: consumeAuthEntrypoint() })

      // Save or clear email in localStorage based on "Remember me" choice.
      try {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_STORAGE_KEY, email)
        } else {
          localStorage.removeItem(REMEMBER_ME_STORAGE_KEY)
        }
      } catch {
        // Ignore storage access issues; auth flow should still succeed.
      }

      if (onLoginSuccess) {
        onLoginSuccess()
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please check your email and password.'

      // TEMPORARILY DISABLED: Don't show inline validation errors for testing
      // Just show the general error message
      setError(errorMessage)
      setEmailError('')
      setPasswordError('')

      console.error('Login error:', err)
      console.error('Error details:', {
        message: err.message,
        validationErrors: err.validationErrors,
        error: err,
      })
    } finally {
      setLoading(false)
    }
  }

  const form = (
        <form id={fieldId('login-form')} onSubmit={handleSubmit} aria-label="Login form">
          {guestKitchenWarning ? (
            <StateBanner
              variant="soft"
              headline="This kitchen won’t be added to that account"
              body="After you log in, you’ll confirm before this kitchen is discarded."
            />
          ) : null}
          <div className="login-form-block">
            <label htmlFor={fieldId('email')} className="login-form-label">
              Username or Email
            </label>
            <Input
              id={fieldId('email')}
              name="email"
              type="text"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError('')
              }}
              required
              disabled={loading}
              invalid={Boolean(emailError)}
              style={{ width: '100%', fontSize: 'var(--text-sm)' }}
              placeholder="name@domain.com"
              autoComplete="username"
            />
            {emailError && (
              <div className="page-alert-error" style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2)' }}>
                <i className="fas fa-exclamation-circle icon-xs icon-error" aria-hidden="true"></i>
                {emailError}
              </div>
            )}
          </div>

          <div className="login-form-block">
            <label htmlFor={fieldId('password')} className="login-form-label">
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <Input
                id={fieldId('password')}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (passwordError) setPasswordError('')
                }}
                required
                disabled={loading}
                invalid={Boolean(passwordError)}
                style={{ width: '100%', paddingRight: '40px' }}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                style={{
                  ...passwordToggleStyle,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </Button>
            </div>
            {passwordError && (
              <div className="page-alert-error" style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2)' }}>
                <i className="fas fa-exclamation-circle icon-xs icon-error" aria-hidden="true"></i>
                {passwordError}
              </div>
            )}
          </div>

          <div className="login-form-block--sm">
              <label
              htmlFor={fieldId('remember-me')}
              className="preferences-checkbox-label"
              style={{
                cursor: loading ? 'not-allowed' : 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                id={fieldId('remember-me')}
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                aria-label="Remember me"
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                Remember me
              </span>
            </label>
          </div>

          {error && (
            <StateBanner
              variant="urgent"
              headline="Couldn’t log you in"
              body={error}
              dismissible
              onDismiss={() => setError('')}
              role="alert"
            />
          )}

          <Button
            id={fieldId('login-submit')}
            type="submit"
            variant="primary"
            disabled={loading || !email || !password}
            className={loading ? 'loading' : ''}
            aria-label={loading ? 'Logging in' : 'Log in'}
            style={{
              width: '100%',
              fontSize: 'var(--text-base)',
              fontWeight: '500',
            }}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Logging in...
              </>
            ) : embedded ? (
              'Log in'
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i> Log In
              </>
            )}
          </Button>

          {typeof onSwitchToRegister === 'function' && (
            <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
              <Button
                type="button"
                variant="link"
                className="link-cta-standalone"
                onClick={() => onSwitchToRegister()}
                disabled={loading}
                style={{ fontSize: 'var(--text-sm)' }}
                aria-label="Go to create account"
              >
                New here? Create an account
              </Button>
            </div>
          )}
        </form>
  )

  if (embedded) return form

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        minHeight: '400px',
      }}
    >
      <Card style={{ maxWidth: '400px', width: '100%', position: 'relative' }}>
        <CardTitle style={{ marginBottom: 'var(--space-6)' }}>
          <i className="fas fa-sign-in-alt icon-sm icon-primary icon-spacing-sm" aria-hidden="true"></i> Login
        </CardTitle>

        <p className="page-subtitle" style={{ marginBottom: 'var(--space-6)', marginTop: 0 }}>
          {onLoginSuccess
            ? 'Log in to save recipes, set your preferences, and make grocery lists.'
            : 'Please log in with an admin account to access configuration management.'}
        </p>
        {form}
      </Card>
    </div>
  )
}

export default Login
